import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Trash2,
  Edit,
  RefreshCw,
  Link,
  Loader2,
  AlertCircle,
  Calendar,
  CheckCircle,
  Settings,
  Clock,
  Filter,
  X,
  Repeat,
  Users,
  Ticket,
  DollarSign,
  ClipboardList,
} from "lucide-react";
import SiteNav from "../components/Layout/SiteNav";
import { useAuth } from "../contexts/AuthContext";
import {
  checkAdmin,
  createEvent,
  updateEvent,
  deleteEvent,
  refreshGoogleCalendarEvents,
  getGoogleCalendars,
  addGoogleCalendar,
  updateGoogleCalendar,
  removeGoogleCalendar,
  getMasjidEvents,
} from "../services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TimePicker } from "@/components/ui/time-picker";
import { format, parse } from "date-fns";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  BRAND,
  EDITORIAL_FONT,
  Eyebrow,
  SerifHeading,
  PillButton,
  EditorialShell,
  EditorialCard,
  PatternTile,
  useEditorialPalette,
} from "../components/editorial/EditorialPrimitives";
const CRM_ENABLED = import.meta.env.VITE_CRM_ENABLED === "true";

// ────────────────────────────────────────────────────────────────────────────────
// Utility Functions
// ────────────────────────────────────────────────────────────────────────────────

const WEEKDAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const PRAYER_NAMES = ["Fajr", "Sunrise", "Dhuhr", "Asr", "Maghrib", "Isha"];

// Format date using UTC to match backend's recurrenceInstanceId format
const formatYMDLocal = (d) => {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Convert 12-hour format (e.g., "10:30 AM") to 24-hour format (e.g., "10:30") for HTML input
const convertTo24Hour = (time12h) => {
  if (!time12h || typeof time12h !== "string") return "";
  const trimmed = time12h.trim();
  
  // If already in 24-hour format (HH:mm), return as-is
  if (/^\d{1,2}:\d{2}$/.test(trimmed)) return trimmed;
  
  // Match 12-hour format: "10:30 AM" or "10:30AM"
  const match = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return trimmed;
  
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = match[3].toUpperCase();
  
  if (period === "PM" && hours !== 12) {
    hours += 12;
  } else if (period === "AM" && hours === 12) {
    hours = 0;
  }
  
  return `${String(hours).padStart(2, "0")}:${minutes}`;
};

// Convert 24-hour format (e.g., "10:30") to 12-hour format (e.g., "10:30 AM") for storage
const convertTo12Hour = (time24h) => {
  if (!time24h || typeof time24h !== "string") return "";
  const trimmed = time24h.trim();
  
  // If already has AM/PM, return as-is
  if (/AM|PM/i.test(trimmed)) return trimmed;
  
  // Match 24-hour format: "10:30" or "22:30"
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  
  const period = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12;
  
  return `${hours12}:${minutes} ${period}`;
};

const toJsDate = (val) => {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (val.toDate && typeof val.toDate === "function") return val.toDate();
  if (val.seconds) return new Date(val.seconds * 1000);
  if (typeof val === "string") return new Date(val);
  return new Date();
};

const formatRecurrenceRule = (recurrence) => {
  if (!recurrence || recurrence.type === "never") return null;
  
  const dayMap = {
    SU: 'Sunday', MO: 'Monday', TU: 'Tuesday', WE: 'Wednesday',
    TH: 'Thursday', FR: 'Friday', SA: 'Saturday'
  };
  
  let baseText = "";
  const interval = recurrence.interval || 1;
  
  if (recurrence.type === "weekly") {
    const days = recurrence.byDay && recurrence.byDay.length > 0 ? recurrence.byDay.map(d => dayMap[d] || d).join(", ") : "";
    if (interval === 1) {
      baseText = days ? `Weekly on ${days}` : "Weekly";
    } else {
      baseText = days ? `Every ${interval} weeks on ${days}` : `Every ${interval} weeks`;
    }
  } else if (recurrence.type === "daily") {
    baseText = interval === 1 ? "Daily" : `Every ${interval} days`;
  } else if (recurrence.type === "monthly") {
    baseText = interval === 1 ? "Monthly" : `Every ${interval} months`;
  } else if (recurrence.type === "yearly") {
    baseText = interval === 1 ? "Yearly" : `Every ${interval} years`;
  } else {
    baseText = "Recurring";
  }
  
  if (recurrence.count) {
    baseText += `, ${recurrence.count} times`;
  } else if (recurrence.until) {
    const untilDate = toJsDate(recurrence.until);
    baseText += ` until ${untilDate.toLocaleDateString()}`;
  }
  
  return baseText;
};

// Detect if recurrence has custom rules
const hasCustomRecurrence = (recurrence) => {
  if (!recurrence || recurrence.type === "never") return false;
  
  // Check for multiple days in weekly
  if (recurrence.type === "weekly" && recurrence.byDay && recurrence.byDay.length > 1) return true;
  
  // Check for monthly custom patterns
  if (recurrence.type === "monthly") {
    if (recurrence.bySetPos && recurrence.bySetPos.length > 0) return true;
    if (recurrence.byMonthDay && recurrence.byMonthDay.length > 1) return true;
  }
  
  // Check for yearly custom patterns
  if (recurrence.type === "yearly") {
    if (recurrence.byMonth && recurrence.byMonth.length > 1) return true;
    if (recurrence.byMonthDay && recurrence.byMonthDay.length > 1) return true;
    if (recurrence.bySetPos && recurrence.bySetPos.length > 0) return true;
  }
  
  return false;
};

// ────────────────────────────────────────────────────────────────────────────────
// Backend ↔ UI mappers for registrationConfig
// Backend (events/index.js create/update) accepts:
//   maxCapacity, requireApproval, allowWaitlist, collectPayment, paymentType,
//   paymentAmount, paymentUrl, ageRestriction, genderRestriction, requireWaiver,
//   waiverText, customFields, allowMultipleAttendees, closesAt
// Frontend UI uses richer field names (maxParticipants, requireAge/minAge/maxAge,
// allowedGenders, waiverRequired, registrationDeadlineHours). These functions
// translate at the API boundary so the modal stays as-is.
//
// Note: ticketTypes / promoCodes / sessions are stored in registrationConfig
// in the UI but are dropped by the create/update endpoint — they need a
// separate persistence path on the backend.
// ────────────────────────────────────────────────────────────────────────────────
const toBackendRegistrationConfig = (uiConfig, eventDate) => {
  if (!uiConfig) return null;
  let closesAt = null;
  if (uiConfig.registrationDeadlineHours && eventDate instanceof Date && !isNaN(eventDate)) {
    closesAt = new Date(eventDate.getTime() - uiConfig.registrationDeadlineHours * 3600000).toISOString();
  }
  const ageRestriction = uiConfig.requireAge
    ? uiConfig.minAge ?? uiConfig.maxAge ?? null
    : null;
  const genderRestriction = uiConfig.requireGender && Array.isArray(uiConfig.allowedGenders) && uiConfig.allowedGenders.length > 0
    ? uiConfig.allowedGenders[0]
    : null;
  return {
    maxCapacity: uiConfig.maxParticipants ?? null,
    requireApproval: !!uiConfig.requireApproval,
    allowWaitlist: !!uiConfig.allowWaitlist,
    collectPayment: !!uiConfig.collectPayment,
    paymentType: uiConfig.paymentType ?? null,
    paymentAmount: uiConfig.paymentAmount ?? null,
    paymentUrl: uiConfig.paymentUrl ?? null,
    ageRestriction,
    genderRestriction,
    requireWaiver: !!uiConfig.waiverRequired,
    waiverText: uiConfig.waiverText || null,
    customFields: Array.isArray(uiConfig.customFields) ? uiConfig.customFields : [],
    allowMultipleAttendees: !!uiConfig.allowMultipleAttendees,
    closesAt,
  };
};

const fromBackendRegistrationConfig = (backendConfig, defaults, eventDate) => {
  if (!backendConfig) return { ...defaults };
  let registrationDeadlineHours = null;
  if (backendConfig.closesAt && eventDate instanceof Date && !isNaN(eventDate)) {
    const closes = new Date(backendConfig.closesAt);
    if (!isNaN(closes)) {
      const diffHours = Math.round((eventDate.getTime() - closes.getTime()) / 3600000);
      if (diffHours > 0) registrationDeadlineHours = diffHours;
    }
  }
  return {
    ...defaults,
    maxParticipants: backendConfig.maxCapacity ?? null,
    requireApproval: !!backendConfig.requireApproval,
    allowWaitlist: !!backendConfig.allowWaitlist,
    collectPayment: !!backendConfig.collectPayment,
    paymentType: backendConfig.paymentType ?? null,
    paymentAmount: backendConfig.paymentAmount ?? null,
    paymentUrl: backendConfig.paymentUrl ?? null,
    requireAge: backendConfig.ageRestriction != null,
    minAge: backendConfig.ageRestriction ?? null,
    maxAge: null,
    requireGender: backendConfig.genderRestriction != null,
    allowedGenders: backendConfig.genderRestriction ? [backendConfig.genderRestriction] : [],
    waiverRequired: !!backendConfig.requireWaiver,
    waiverText: backendConfig.waiverText || "",
    customFields: Array.isArray(backendConfig.customFields) ? backendConfig.customFields : [],
    allowMultipleAttendees: !!backendConfig.allowMultipleAttendees,
    registrationDeadlineHours,
    ticketTypes: Array.isArray(backendConfig.ticketTypes) ? backendConfig.ticketTypes : [],
  };
};

export default function Events() {
  const palette = useEditorialPalette();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [adminInfo, setAdminInfo] = useState(null);
  const [events, setEvents] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dialogs
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [calendarSettingsOpen, setCalendarSettingsOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newCalendarId, setNewCalendarId] = useState("");
  
  // Calendar settings
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [calendarNickname, setCalendarNickname] = useState("");
  const [calendarTimeOffset, setCalendarTimeOffset] = useState(0);
  const [calendarFilters, setCalendarFilters] = useState([]);
  const [newFilterKeyword, setNewFilterKeyword] = useState("");
  const [newFilterPartial, setNewFilterPartial] = useState(true);
  const [newFilterCaseSensitive, setNewFilterCaseSensitive] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Default registration config
  const defaultRegistrationConfig = {
    requireApproval: true,
    maxParticipants: null,
    requireAge: false,
    minAge: null,
    maxAge: null,
    requireGender: false,
    allowedGenders: [],
    waiverRequired: false,
    waiverText: "",
    customFields: [],
    confirmationMessage: "",
    allowWaitlist: false,
    registrationDeadlineHours: null,
    ticketTypes: [],
    promoCodes: [],
    sessions: [],
    requireSessionSelection: false,
  };

  // Form data
  const [eventData, setEventData] = useState({
    title: "",
    date: "",
    time: "",
    endTime: "",
    location: "",
    description: "",
    timeType: "manual", // "manual" | "prayer"
    // Event mode: "normal" (with optional RSVP) or "registration_required" (PRO feature)
    eventMode: "normal",
    // Registration configuration (only used when eventMode is "registration_required")
    registrationConfig: { ...defaultRegistrationConfig },
    prayerTimeConfig: {
      startType: "prayer",
      startPrayer: "Fajr",
      startPrayerTimeType: "adhan",
      startOffsetMinutes: 0,
      startOffsetType: "minutes",
      endType: "duration",
      endPrayer: "Fajr",
      endPrayerTimeType: "adhan",
      endOffsetMinutes: 0,
      endOffsetType: "minutes",
      durationMinutes: 60,
    },
    recurrence: {
      type: "never",
      interval: 1,
      byDay: [],
      byMonthDay: [],
      bySetPos: [],
      byMonth: [],
      until: null,
      count: null,
    },
  });

  // Edit/Delete scope for recurring events
  const [editScopeDialogOpen, setEditScopeDialogOpen] = useState(false);
  const [deleteScopeDialogOpen, setDeleteScopeDialogOpen] = useState(false);
  const [pendingEditEvent, setPendingEditEvent] = useState(null);
  const [pendingDeleteEventId, setPendingDeleteEventId] = useState(null);
  const [editScope, setEditScope] = useState("single");
  const [deleteScope, setDeleteScope] = useState("single");

  // Final delete confirmation
  const [pendingDeletion, setPendingDeletion] = useState(null);
  const [deletingNow, setDeletingNow] = useState(false);

  // Load admin info and events
  useEffect(() => {
    const loadData = async () => {
      if (!user?.email) return;

      try {
        setLoading(true);
        setError(null);

        // Check admin status
        const adminData = await checkAdmin(user.email);
        if (!adminData.isAdmin) {
          setError("You do not have admin privileges");
          return;
        }

        setAdminInfo(adminData);

        // Get masjid ID
        const masjidId =
          adminData.role === "super"
            ? adminData.activeMasjidId
            : adminData.masjidId;

        if (!masjidId) {
          setError("No masjid assigned. Please contact system administrator.");
          return;
        }

        // Load events
        await loadEvents(masjidId);

        // Load Google Calendars
        await loadCalendars(masjidId);
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Failed to load data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  // Load events for masjid via API
  const loadEvents = async (masjidId) => {
    try {
      const response = await getMasjidEvents(masjidId, { includePast: false });
      setEvents(response.events || []);
    } catch (err) {
      console.error("Error loading events:", err);
    }
  };

  // Load Google Calendars
  const loadCalendars = async (masjidId) => {
    try {
      const result = await getGoogleCalendars(masjidId);
      if (result.calendars) {
        setCalendars(result.calendars);
      }
    } catch (err) {
      console.error("Error loading calendars:", err);
    }
  };

  // Refresh events from Google Calendar
  const handleRefreshGoogleCalendar = async () => {
    const masjidId =
      adminInfo?.role === "super"
        ? adminInfo.activeMasjidId
        : adminInfo?.masjidId;

    if (!masjidId) return;

    try {
      setRefreshing(true);
      setError(null);
      setSuccess(null);

      const result = await refreshGoogleCalendarEvents(masjidId);

      const message = result.deleted > 0
        ? `Successfully synced ${result.updated} events and removed ${result.deleted} deleted events`
        : `Successfully synced ${result.updated} events from Google Calendar`;
      
      setSuccess(message);

      // Reload events
      await loadEvents(masjidId);
    } catch (err) {
      console.error("Error refreshing events:", err);
      setError(
        err.response?.data?.error ||
          "Failed to refresh events. Please try again."
      );
    } finally {
      setRefreshing(false);
    }
  };

  // Open calendar settings
  const handleOpenCalendarSettings = (calendar) => {
    setSelectedCalendar(calendar);
    setCalendarNickname(calendar.nickname || "");
    setCalendarTimeOffset(calendar.timeOffset || 0);
    setCalendarFilters(calendar.filters || []);
    setNewFilterKeyword("");
    setNewFilterPartial(true);
    setNewFilterCaseSensitive(false);
    // Keep manage dialog open in background
    setCalendarSettingsOpen(true);
  };

  // Save calendar settings
  const handleSaveCalendarSettings = async () => {
    const masjidId =
      adminInfo?.role === "super"
        ? adminInfo.activeMasjidId
        : adminInfo?.masjidId;

    if (!masjidId || !selectedCalendar) return;

    try {
      setSettingsSaving(true);
      setError(null);
      setSuccess(null);

      await updateGoogleCalendar(masjidId, selectedCalendar.calendarId, {
        timeOffset: calendarTimeOffset,
        nickname: calendarNickname.trim() || null,
        filters: calendarFilters,
      });

      setSuccess("Calendar settings updated successfully!");
      setCalendarSettingsOpen(false);
      setSelectedCalendar(null);

      // Reload calendars and events
      await loadCalendars(masjidId);
      await loadEvents(masjidId);
    } catch (err) {
      console.error("Error updating calendar settings:", err);
      setError(
        err.response?.data?.error ||
          "Failed to update calendar settings. Please try again."
      );
    } finally {
      setSettingsSaving(false);
    }
  };

  // Add filter
  const handleAddFilter = () => {
    const keyword = newFilterKeyword.trim();
    if (!keyword) return;

    setCalendarFilters([
      ...calendarFilters,
      {
        keyword,
        partial: newFilterPartial,
        caseSensitive: newFilterCaseSensitive,
      },
    ]);
    setNewFilterKeyword("");
    setNewFilterPartial(true);
    setNewFilterCaseSensitive(false);
  };

  // Remove filter
  const handleRemoveFilter = (index) => {
    setCalendarFilters(calendarFilters.filter((_, i) => i !== index));
  };

  // Add Google Calendar
  const handleAddCalendar = async () => {
    const masjidId =
      adminInfo?.role === "super"
        ? adminInfo.activeMasjidId
        : adminInfo?.masjidId;

    if (!masjidId || !newCalendarId.trim()) return;

    try {
      setError(null);
      setSuccess(null);

      const result = await addGoogleCalendar(masjidId, newCalendarId.trim());

      setSuccess(
        `Calendar added successfully! Synced ${result.updated} events.`
      );
      setNewCalendarId("");
      setCalendarDialogOpen(false);

      // Reload calendars and events
      await loadCalendars(masjidId);
      await loadEvents(masjidId);
    } catch (err) {
      console.error("Error adding calendar:", err);
      setError(
        err.response?.data?.error || "Failed to add calendar. Please try again."
      );
    }
  };

  // Remove Google Calendar
  const handleRemoveCalendar = async (calendarId) => {
    const masjidId =
      adminInfo?.role === "super"
        ? adminInfo.activeMasjidId
        : adminInfo?.masjidId;

    if (!masjidId) return;

    if (
      !confirm(
        "Are you sure you want to remove this calendar? All associated events will be deleted."
      )
    ) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      const result = await removeGoogleCalendar(masjidId, calendarId);

      setSuccess(
        `Calendar removed successfully. Deleted ${result.deleted} events.`
      );

      // Reload calendars and events
      await loadCalendars(masjidId);
      await loadEvents(masjidId);
    } catch (err) {
      console.error("Error removing calendar:", err);
      setError(
        err.response?.data?.error ||
          "Failed to remove calendar. Please try again."
      );
    }
  };

  // Save event (create or update)
  const handleSaveEvent = async () => {
    const masjidId =
      adminInfo?.role === "super"
        ? adminInfo.activeMasjidId
        : adminInfo?.masjidId;

    if (!masjidId) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Validate
      if (
        !eventData.title ||
        !eventData.date
      ) {
        setError("Please fill in all required fields (Title and Date)");
        return;
      }

      // Additional validation for manual time type
      if (eventData.timeType === "manual" && !eventData.time) {
        setError("Please specify a start time or use prayer-based time");
        return;
      }

      // Convert date to timestamp
      const eventDate = new Date(eventData.date);
      if (eventData.time) {
        const [hours, minutes] = eventData.time.split(":");
        eventDate.setHours(parseInt(hours), parseInt(minutes));
      }

      // Convert times from 24-hour (HTML input) to 12-hour format (for DB storage)
      const time12h = eventData.time ? convertTo12Hour(eventData.time) : null;
      const endTime12h = eventData.endTime ? convertTo12Hour(eventData.endTime) : null;

      const payload = {
        title: eventData.title,
        date: eventDate,
        time: time12h,
        endTime: endTime12h,
        location: eventData.location,
        description: eventData.description,
        timeType: eventData.timeType,
        prayerTimeConfig: eventData.timeType === "prayer" ? eventData.prayerTimeConfig : null,
        recurrence: eventData.recurrence.type !== "never" ? eventData.recurrence : null,
        // PRO Registration features
        eventMode: eventData.eventMode,
        registrationConfig: eventData.eventMode === "registration_required"
          ? toBackendRegistrationConfig(eventData.registrationConfig, eventDate)
          : null,
      };

      if (editingEvent) {
        // Update existing event
        await updateEvent(editingEvent.id, payload, editScope);
        setSuccess("Event updated successfully!");
      } else {
        // Create new event
        await createEvent(masjidId, payload);
        setSuccess("Event created successfully!");
      }

      setEventDialogOpen(false);
      setEditingEvent(null);
      setEventData({
        title: "",
        date: "",
        time: "",
        endTime: "",
        location: "",
        description: "",
        timeType: "manual",
        eventMode: "normal",
        registrationConfig: { ...defaultRegistrationConfig },
        prayerTimeConfig: {
          startType: "prayer",
          startPrayer: "Fajr",
          startPrayerTimeType: "adhan",
          startOffsetMinutes: 0,
          startOffsetType: "minutes",
          endType: "duration",
          endPrayer: "Fajr",
          endPrayerTimeType: "adhan",
          endOffsetMinutes: 0,
          endOffsetType: "minutes",
          durationMinutes: 60,
        },
        recurrence: {
          type: "never",
          interval: 1,
          byDay: [],
          byMonthDay: [],
          bySetPos: [],
          byMonth: [],
          until: null,
          count: null,
        },
      });

      // Reload events
      await loadEvents(masjidId);
    } catch (err) {
      console.error("Error saving event:", err);
      setError(
        err.response?.data?.error || "Failed to save event. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // Delete event — opens the appropriate dialog. Recurring events first go through
  // the scope picker; both paths land in the final confirmation dialog.
  const handleDeleteEvent = (eventId, event) => {
    const isRecurring = event?.recurrence && event.recurrence.type !== "never";

    if (isRecurring) {
      setPendingDeleteEventId(eventId);
      setDeleteScopeDialogOpen(true);
    } else {
      setPendingDeletion({
        eventId,
        scope: "single",
        title: event?.title || "this event",
        isRecurring: false,
      });
    }
  };

  // Run the actual deletion once the user confirms in the modal.
  const executePendingDelete = async () => {
    if (!pendingDeletion) return;
    const { eventId, scope } = pendingDeletion;
    setDeletingNow(true);
    try {
      setError(null);
      setSuccess(null);
      await deleteEvent(eventId, scope);
      setSuccess(`${scope === "series" ? "Series" : "Event"} deleted successfully!`);
      const masjidId =
        adminInfo?.role === "super"
          ? adminInfo.activeMasjidId
          : adminInfo?.masjidId;
      await loadEvents(masjidId);
      setPendingDeletion(null);
    } catch (err) {
      console.error("Error deleting event:", err);
      setError(
        err.response?.data?.error || "Failed to delete event. Please try again."
      );
    } finally {
      setDeletingNow(false);
    }
  };

  // Open edit dialog
  const handleEditEvent = (event) => {
    // Check if event is recurring
    const isRecurring = event.recurrence && event.recurrence.type !== "never";
    
    if (isRecurring) {
      // Show scope selection dialog
      setPendingEditEvent(event);
      setEditScopeDialogOpen(true);
      return;
    }

    // Non-recurring, edit directly
    openEditDialog(event, "single");
  };

  // Open edit dialog with selected scope
  const openEditDialog = (event, scope) => {
    const eventDate = event.date?.toDate
      ? event.date.toDate()
      : new Date(event.date);
    const dateStr = eventDate.toISOString().split("T")[0];

    setEditingEvent(event);
    setEditScope(scope);
    setEventData({
      title: event.title || "",
      date: dateStr,
      // Convert 12-hour format (from DB) to 24-hour format (for HTML input)
      time: convertTo24Hour(event.time) || "",
      endTime: convertTo24Hour(event.endTime) || "",
      location: event.location || "",
      description: event.description || "",
      timeType: event.timeType || "manual",
      eventMode: event.eventMode || "normal",
      registrationConfig: fromBackendRegistrationConfig(
        event.registrationConfig,
        defaultRegistrationConfig,
        event.date ? new Date(event.date) : null
      ),
      prayerTimeConfig: event.prayerTimeConfig || {
        startType: "prayer",
        startPrayer: "Fajr",
        startPrayerTimeType: "adhan",
        startOffsetMinutes: 0,
        startOffsetType: "minutes",
        endType: "duration",
        endPrayer: "Fajr",
        endPrayerTimeType: "adhan",
        endOffsetMinutes: 0,
        endOffsetType: "minutes",
        durationMinutes: 60,
      },
      recurrence: (() => {
        const rec = event.recurrence || {
          type: "never",
          interval: 1,
          byDay: [],
          byMonthDay: [],
          bySetPos: [],
          byMonth: [],
          until: null,
          count: null,
          showCustomOptions: false,
        };
        // Detect if custom recurrence and set showCustomOptions
        if (hasCustomRecurrence(rec) && !rec.showCustomOptions) {
          rec.showCustomOptions = true;
        }
        return rec;
      })(),
    });
    setEventDialogOpen(true);
  };

  // Confirm edit scope for recurring events
  const confirmEditScope = (scope) => {
    if (!pendingEditEvent) return;
    setEditScopeDialogOpen(false);
    openEditDialog(pendingEditEvent, scope);
    setPendingEditEvent(null);
  };

  // Pick scope for recurring delete, then hand off to the confirmation modal.
  const confirmDeleteScope = (scope) => {
    if (!pendingDeleteEventId) return;
    const event = events.find((e) => e.id === pendingDeleteEventId);
    setDeleteScopeDialogOpen(false);
    setPendingDeletion({
      eventId: pendingDeleteEventId,
      scope,
      title: event?.title || "this event",
      isRecurring: true,
    });
    setPendingDeleteEventId(null);
  };

  // Open create dialog
  const handleCreateEvent = () => {
    setEditingEvent(null);
    setEventData({
      title: "",
      date: "",
      time: "",
      endTime: "",
      location: "",
      description: "",
      timeType: "manual",
      eventMode: "normal",
      registrationConfig: { ...defaultRegistrationConfig },
      prayerTimeConfig: {
        startType: "prayer",
        startPrayer: "Fajr",
        startPrayerTimeType: "adhan",
        startOffsetMinutes: 0,
        startOffsetType: "minutes",
        endType: "duration",
        endPrayer: "Fajr",
        endPrayerTimeType: "adhan",
        endOffsetMinutes: 0,
        endOffsetType: "minutes",
        durationMinutes: 60,
      },
      recurrence: {
        type: "never",
        interval: 1,
        byDay: [],
        byMonthDay: [],
        bySetPos: [],
        byMonth: [],
        until: null,
        count: null,
        showCustomOptions: false,
      },
    });
    setEventDialogOpen(true);
  };

  if (loading) {
    return (
      <EditorialShell>
        <SiteNav />
        <section style={{ background: palette.ivory }}>
          <div
            className="mx-auto flex flex-col items-center justify-center px-4 py-24 sm:px-6 lg:px-10"
            style={{ maxWidth: 1320 }}
          >
            <div
              className="flex items-center gap-2 text-sm"
              style={{ color: palette.muted, fontFamily: EDITORIAL_FONT.sans }}
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading events…
            </div>
          </div>
        </section>
      </EditorialShell>
    );
  }

  return (
    <EditorialShell>
      <SiteNav />

      {/* ══════════════════════════════════
          HERO / HEADER
          ══════════════════════════════════ */}
      <section
        className="relative overflow-hidden"
        style={{ background: palette.ivory }}
      >
        <PatternTile opacity={0.04} />
        <div
          className="relative mx-auto px-4 pt-16 pb-12 sm:px-6 sm:pt-20 sm:pb-16 lg:px-10 lg:pt-24 lg:pb-20"
          style={{ maxWidth: 1320 }}
        >
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <Eyebrow>Events management</Eyebrow>
              <SerifHeading
                level={1}
                className="mt-6"
                style={{
                  fontSize: "clamp(2.5rem, 6vw, 5rem)",
                  lineHeight: 1.08,
                  letterSpacing: "-0.035em",
                }}
              >
                Events, synced &
                <br />
                <em style={{ color: BRAND.gold, fontWeight: 400 }}>scheduled.</em>
              </SerifHeading>
              <p
                className="mt-6 max-w-xl text-base sm:text-lg"
                style={{ color: palette.ink2, lineHeight: 1.55, fontFamily: EDITORIAL_FONT.sans }}
              >
                Manage and sync your community events: fundraisers, halaqas, Eid
                prayers, and youth nights, all from one quiet calendar.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <PillButton
                variant="outline"
                onClick={() => setCalendarDialogOpen(true)}
              >
                <Link className="h-4 w-4" />
                Calendars
              </PillButton>
              <PillButton
                variant="ghost"
                onClick={handleRefreshGoogleCalendar}
                disabled={refreshing || calendars.length === 0}
              >
                <RefreshCw
                  className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
                />
                {refreshing ? "Syncing…" : "Sync"}
              </PillButton>
              <PillButton variant="primary" onClick={handleCreateEvent}>
                <Plus className="h-4 w-4" />
                Create event
              </PillButton>
            </div>
          </div>

          {/* Alerts */}
          {(error || success || calendars.length > 0) && (
            <div className="mt-10 space-y-3">
              {error && (
                <div
                  className="flex items-center gap-3 rounded-2xl p-4"
                  style={{
                    background: BRAND.terracotta + "12",
                    border: `1px solid ${BRAND.terracotta}40`,
                    color: BRAND.terracotta,
                  }}
                >
                  <AlertCircle className="h-5 w-5 shrink-0" />
                  <p className="text-sm font-medium" style={{ fontFamily: EDITORIAL_FONT.sans }}>
                    {error}
                  </p>
                </div>
              )}

              {success && (
                <div
                  className="flex items-center gap-3 rounded-2xl p-4"
                  style={{
                    background: BRAND.sage + "12",
                    border: `1px solid ${BRAND.sage}40`,
                    color: BRAND.sage,
                  }}
                >
                  <CheckCircle className="h-5 w-5 shrink-0" />
                  <p className="text-sm font-medium" style={{ fontFamily: EDITORIAL_FONT.sans }}>
                    {success}
                  </p>
                </div>
              )}

              {calendars.length > 0 && (
                <div
                  className="flex items-center gap-3 rounded-2xl p-4"
                  style={{
                    background: BRAND.gold + "10",
                    border: `1px solid ${BRAND.gold}40`,
                    color: BRAND.goldDeep,
                  }}
                >
                  <Link className="h-5 w-5 shrink-0" />
                  <p className="text-sm font-medium" style={{ fontFamily: EDITORIAL_FONT.sans }}>
                    {calendars.length} Google Calendar(s) connected and syncing
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ══════════════════════════════════
          EVENTS LIST
          ══════════════════════════════════ */}
      <section style={{ background: palette.paper }}>
        <div
          className="mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-10 lg:py-24"
          style={{ maxWidth: 1320 }}
        >
          <div
            className="mb-10 flex items-end justify-between border-t pt-8"
            style={{ borderColor: palette.hairline }}
          >
            <div>
              <Eyebrow>Calendar</Eyebrow>
              <SerifHeading
                level={2}
                className="mt-4"
                style={{
                  fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)",
                  letterSpacing: "-0.03em",
                  lineHeight: 1,
                }}
              >
                Upcoming{" "}
                <em style={{ color: BRAND.gold, fontWeight: 400 }}>events.</em>
              </SerifHeading>
            </div>
            <span
              className="text-[11px] font-semibold uppercase"
              style={{
                letterSpacing: "0.16em",
                color: palette.muted,
                fontFamily: EDITORIAL_FONT.sans,
              }}
            >
              {events.length} {events.length === 1 ? "event" : "events"}
            </span>
          </div>

          {events.length > 0 ? (
            <div className="space-y-3">
              {events.map((event) => {
                const eventDate = event.date?.toDate
                  ? event.date.toDate()
                  : new Date(event.date);
                const isGoogleEvent = event.source?.type === "gcal";

                return (
                  <EditorialCard
                    variant="ivory"
                    key={event.recurrenceInstanceId || event.id}
                    className="group p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_0_1px_rgba(191,154,47,0.45),0_10px_40px_-6px_rgba(191,154,47,0.45)]"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex min-w-0 flex-1 gap-4">
                        {/* Date Badge */}
                        <div
                          className="flex shrink-0 flex-col items-center justify-center rounded-xl px-3 py-2.5 text-center"
                          style={{
                            background: palette.paper,
                            border: `1px solid ${BRAND.gold}30`,
                          }}
                        >
                          <span
                            className="text-[10px] font-semibold uppercase"
                            style={{
                              letterSpacing: "0.14em",
                              color: BRAND.gold,
                            }}
                          >
                            {eventDate.toLocaleDateString("en-US", { month: "short" })}
                          </span>
                          <span
                            style={{
                              fontFamily: EDITORIAL_FONT.serif,
                              fontSize: 26,
                              fontWeight: 500,
                              letterSpacing: "-0.02em",
                              lineHeight: 1,
                              color: palette.ink,
                            }}
                          >
                            {eventDate.getDate()}
                          </span>
                        </div>

                        {/* Event Details */}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3
                              className="truncate"
                              style={{
                                fontFamily: EDITORIAL_FONT.serif,
                                fontSize: 20,
                                fontWeight: 500,
                                letterSpacing: "-0.01em",
                                color: palette.ink,
                              }}
                            >
                              {event.title}
                            </h3>
                            {event.recurrence && event.recurrence.type !== "never" && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
                                style={{
                                  background: palette.paper,
                                  color: palette.ink2,
                                  border: `1px solid ${palette.hairline}`,
                                  letterSpacing: "0.08em",
                                }}
                              >
                                <Repeat className="h-3 w-3" />
                                Recurring
                              </span>
                            )}
                            {event.timeType === "prayer" && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
                                style={{
                                  background: BRAND.gold + "18",
                                  color: BRAND.goldDeep,
                                  border: `1px solid ${BRAND.gold}40`,
                                  letterSpacing: "0.08em",
                                }}
                              >
                                <Clock className="h-3 w-3" />
                                Prayer-based
                              </span>
                            )}
                            {event.eventMode === "registration_required" && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
                                style={{
                                  background: BRAND.sage + "18",
                                  color: BRAND.sage,
                                  border: `1px solid ${BRAND.sage}40`,
                                  letterSpacing: "0.08em",
                                }}
                              >
                                <ClipboardList className="h-3 w-3" />
                                Registration
                              </span>
                            )}
                          </div>

                          <div
                            className="mt-1.5 flex flex-wrap items-center gap-3 text-sm"
                            style={{ color: palette.muted }}
                          >
                            {event.time && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                {event.time}
                                {event.endTime && ` – ${event.endTime}`}
                              </span>
                            )}
                            {event.location && (
                              <span className="truncate">{event.location}</span>
                            )}
                          </div>

                          {event.recurrence && event.recurrence.type !== "never" && (
                            <p
                              className="mt-1 text-xs italic"
                              style={{
                                color: palette.muted,
                                fontFamily: EDITORIAL_FONT.serif,
                                fontSize: 13,
                              }}
                            >
                              {formatRecurrenceRule(event.recurrence)}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Actions & Source */}
                      <div className="flex shrink-0 items-center gap-2">
                        <span
                          className="inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase"
                          style={{
                            background: isGoogleEvent ? palette.ink : palette.paper,
                            color: isGoogleEvent ? palette.ivory : palette.ink2,
                            border: `1px solid ${isGoogleEvent ? palette.ink : palette.hairline}`,
                            letterSpacing: "0.1em",
                          }}
                        >
                          {isGoogleEvent ? "Google" : "Manual"}
                        </span>

                        {!isGoogleEvent && (
                          <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                            {event.eventMode === "registration_required" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-full"
                                style={{ color: BRAND.sage }}
                                onClick={() => navigate(`/events/${event.id}/registrations`)}
                                title="Manage registrations"
                              >
                                <Users className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-full"
                              style={{ color: BRAND.gold }}
                              onClick={() => handleEditEvent(event)}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-full"
                              style={{ color: BRAND.terracotta }}
                              onClick={() => handleDeleteEvent(event.id, event)}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </EditorialCard>
                );
              })}
            </div>
          ) : (
            <EditorialCard
              variant="ivory"
              className="p-12 text-center"
              style={{ border: `1px dashed ${BRAND.gold}60` }}
            >
              <SerifHeading
                level={3}
                className="mb-3"
                style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", lineHeight: 1, letterSpacing: "-0.03em" }}
              >
                No upcoming{" "}
                <em style={{ color: BRAND.gold, fontWeight: 400 }}>events.</em>
              </SerifHeading>
              <p
                className="mx-auto mb-7 max-w-sm text-sm sm:text-base"
                style={{ color: palette.ink2, lineHeight: 1.55, fontFamily: EDITORIAL_FONT.sans }}
              >
                Create an event or sync from Google Calendar.
              </p>
              <PillButton variant="primary" onClick={handleCreateEvent}>
                <Plus className="h-4 w-4" />
                Create event
              </PillButton>
            </EditorialCard>
          )}
        </div>
      </section>

      {/* Create/Edit Event Dialog */}
      <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="shrink-0">
              <DialogTitle
                style={{
                  fontFamily: EDITORIAL_FONT.serif,
                  fontSize: 28,
                  fontWeight: 500,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                }}
              >
                {editingEvent ? (
                  <>
                    Edit <em style={{ color: BRAND.gold, fontWeight: 400 }}>event.</em>
                  </>
                ) : (
                  <>
                    Create <em style={{ color: BRAND.gold, fontWeight: 400 }}>event.</em>
                  </>
                )}
              </DialogTitle>
              <DialogDescription style={{ color: palette.muted }}>
                {editingEvent
                  ? "Update the event details below."
                  : "Fill in the details for your community event."}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-6 py-4 pr-2">
              {/* Basic Info Section */}
              <section className="space-y-4">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase" style={{ letterSpacing: "0.14em", color: BRAND.gold }}>
                  <Calendar className="h-4 w-4" />
                  Basic Information
                </div>
                <div className="space-y-4 rounded-xl border bg-muted/30 p-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Event Title <span className="text-destructive">*</span></Label>
                    <Input
                      id="title"
                      value={eventData.title}
                      onChange={(e) =>
                        setEventData({ ...eventData, title: e.target.value })
                      }
                      placeholder="e.g., Weekly Quran Study Circle"
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="date">Date <span className="text-destructive">*</span></Label>
                      {(() => {
                        const todayStart = new Date();
                        todayStart.setHours(0, 0, 0, 0);
                        const todayStr = format(todayStart, "yyyy-MM-dd");
                        const dateInPast = !!eventData.date && eventData.date < todayStr;
                        return (
                          <>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  id="date"
                                  type="button"
                                  variant="outline"
                                  className={cn(
                                    "w-full justify-start font-normal",
                                    dateInPast && "border-destructive text-destructive hover:text-destructive focus-visible:ring-destructive"
                                  )}
                                  aria-invalid={dateInPast || undefined}
                                >
                                  <Calendar
                                    className="mr-2 h-4 w-4"
                                    style={{ color: dateInPast ? undefined : BRAND.gold }}
                                  />
                                  {eventData.date ? (
                                    format(parse(eventData.date, "yyyy-MM-dd", new Date()), "PPP")
                                  ) : (
                                    <span className="text-muted-foreground">Pick a date</span>
                                  )}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0 z-[100001]" align="start">
                                <CalendarPicker
                                  mode="single"
                                  selected={
                                    eventData.date
                                      ? parse(eventData.date, "yyyy-MM-dd", new Date())
                                      : undefined
                                  }
                                  onSelect={(date) =>
                                    setEventData({
                                      ...eventData,
                                      date: date ? format(date, "yyyy-MM-dd") : "",
                                    })
                                  }
                                  disabled={{ before: todayStart }}
                                  autoFocus
                                />
                              </PopoverContent>
                            </Popover>
                            {dateInPast && (
                              <p className="text-xs text-destructive">
                                Events can&apos;t be created in the past.
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </section>

              {/* Time Section */}
              <section className="space-y-4">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase" style={{ letterSpacing: "0.14em", color: BRAND.gold }}>
                  <Clock className="h-4 w-4" />
                  Time Settings
                </div>
                <div className="space-y-4 rounded-xl border bg-muted/30 p-4">
                  {/* Time Type Selection */}
                  <div className="space-y-3">
                    <Label className="text-xs text-muted-foreground uppercase">Time Type</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setEventData({ ...eventData, timeType: "manual" })}
                        className={`rounded-lg border-2 p-3 text-left transition-all ${
                          eventData.timeType === "manual"
                            ? "border-primary bg-primary/5"
                            : "border-transparent bg-background hover:border-muted-foreground/30"
                        }`}
                      >
                        <div className="font-medium text-sm">Manual Time</div>
                        <div className="text-xs text-muted-foreground">Set a specific time</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setEventData({ ...eventData, timeType: "prayer" })}
                        className={`rounded-lg border-2 p-3 text-left transition-all ${
                          eventData.timeType === "prayer"
                            ? "border-primary bg-primary/5"
                            : "border-transparent bg-background hover:border-muted-foreground/30"
                        }`}
                      >
                        <div className="font-medium text-sm">Prayer-Based</div>
                        <div className="text-xs text-muted-foreground">Linked to prayer times</div>
                      </button>
                    </div>
                  </div>

                  {/* Manual Time Inputs */}
                  {eventData.timeType === "manual" && (
                    <div className="grid gap-4 sm:grid-cols-2 pt-2">
                      <div className="space-y-2">
                        <Label htmlFor="time">Start Time <span className="text-destructive">*</span></Label>
                        <TimePicker
                          id="time"
                          value={eventData.time}
                          onChange={(v) => setEventData({ ...eventData, time: v })}
                          prefer="businessHours"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="endTime">End Time</Label>
                        {(() => {
                          const endBeforeStart =
                            !!eventData.time &&
                            !!eventData.endTime &&
                            eventData.endTime < eventData.time;
                          return (
                            <>
                              <TimePicker
                                id="endTime"
                                value={eventData.endTime}
                                onChange={(v) => setEventData({ ...eventData, endTime: v })}
                                referenceTime={eventData.time}
                                prefer="after"
                                error={endBeforeStart}
                              />
                              {endBeforeStart && (
                                <p className="text-xs text-destructive">
                                  End time is before the start time.
                                </p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Prayer Time Config */}
                  {eventData.timeType === "prayer" && (
                    <div className="space-y-4 p-4 border rounded-xl bg-blue-50/50 dark:bg-blue-950/20 mt-3">
                      <h4 className="font-semibold text-sm flex items-center gap-2">
                        <Clock className="h-4 w-4 text-blue-600" />
                        Prayer Time Configuration
                      </h4>
                  
                  <div className="space-y-3">
                    <Label>Start Time</Label>
                    <Select
                      value={eventData.prayerTimeConfig.startPrayer}
                      onValueChange={(value) =>
                        setEventData({
                          ...eventData,
                          prayerTimeConfig: {
                            ...eventData.prayerTimeConfig,
                            startPrayer: value,
                          },
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent position="popper">
                        {PRAYER_NAMES.map((prayer) => (
                          <SelectItem key={prayer} value={prayer}>
                            {prayer}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {eventData.prayerTimeConfig.startPrayer !== "Sunrise" && (
                      <Select
                        value={eventData.prayerTimeConfig.startPrayerTimeType}
                        onValueChange={(value) =>
                          setEventData({
                            ...eventData,
                            prayerTimeConfig: {
                              ...eventData.prayerTimeConfig,
                              startPrayerTimeType: value,
                            },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent position="popper">
                          <SelectItem value="adhan">Adhan</SelectItem>
                          <SelectItem value="iqama">Iqama</SelectItem>
                        </SelectContent>
                      </Select>
                    )}

                    <div className="space-y-2">
                      <Label>Offset</Label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={eventData.prayerTimeConfig.startOffsetType === "minutes" ? "default" : "outline"}
                          className="flex-1"
                          onClick={() =>
                            setEventData({
                              ...eventData,
                              prayerTimeConfig: {
                                ...eventData.prayerTimeConfig,
                                startOffsetType: "minutes",
                              },
                            })
                          }
                        >
                          Minutes
                        </Button>
                        <Button
                          type="button"
                          variant={eventData.prayerTimeConfig.startOffsetType === "round" ? "default" : "outline"}
                          className="flex-1"
                          onClick={() =>
                            setEventData({
                              ...eventData,
                              prayerTimeConfig: {
                                ...eventData.prayerTimeConfig,
                                startOffsetType: "round",
                              },
                            })
                          }
                        >
                          Round Down
                        </Button>
                      </div>
                      {eventData.prayerTimeConfig.startOffsetType === "minutes" && (
                        <div className="grid grid-cols-3 gap-2">
                          {[-30, -15, 0, 15, 30, 60].map((offset) => (
                            <Button
                              key={offset}
                              type="button"
                              variant={eventData.prayerTimeConfig.startOffsetMinutes === offset ? "default" : "outline"}
                              size="sm"
                              onClick={() =>
                                setEventData({
                                  ...eventData,
                                  prayerTimeConfig: {
                                    ...eventData.prayerTimeConfig,
                                    startOffsetMinutes: offset,
                                  },
                                })
                              }
                            >
                              {offset === 0 ? "Exactly" : offset > 0 ? `+${offset}` : offset}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>End Time</Label>
                    <Select
                      value={eventData.prayerTimeConfig.endType}
                      onValueChange={(value) =>
                        setEventData({
                          ...eventData,
                          prayerTimeConfig: {
                            ...eventData.prayerTimeConfig,
                            endType: value,
                          },
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent position="popper">
                        <SelectItem value="duration">After Duration</SelectItem>
                        <SelectItem value="prayer">At Prayer Time</SelectItem>
                      </SelectContent>
                    </Select>

                    {eventData.prayerTimeConfig.endType === "duration" && (
                      <div className="space-y-2">
                        <Label>Duration (minutes)</Label>
                        <div className="grid grid-cols-3 gap-2">
                          {[30, 60, 90, 120, 180].map((duration) => (
                            <Button
                              key={duration}
                              type="button"
                              variant={eventData.prayerTimeConfig.durationMinutes === duration ? "default" : "outline"}
                              size="sm"
                              onClick={() =>
                                setEventData({
                                  ...eventData,
                                  prayerTimeConfig: {
                                    ...eventData.prayerTimeConfig,
                                    durationMinutes: duration,
                                  },
                                })
                              }
                            >
                              {duration}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {eventData.prayerTimeConfig.endType === "prayer" && (
                      <>
                        <Select
                          value={eventData.prayerTimeConfig.endPrayer}
                          onValueChange={(value) =>
                            setEventData({
                              ...eventData,
                              prayerTimeConfig: {
                                ...eventData.prayerTimeConfig,
                                endPrayer: value,
                              },
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent position="popper">
                            {PRAYER_NAMES.map((prayer) => (
                              <SelectItem key={prayer} value={prayer}>
                                {prayer}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {eventData.prayerTimeConfig.endPrayer !== "Sunrise" && (
                          <Select
                            value={eventData.prayerTimeConfig.endPrayerTimeType}
                            onValueChange={(value) =>
                              setEventData({
                                ...eventData,
                                prayerTimeConfig: {
                                  ...eventData.prayerTimeConfig,
                                  endPrayerTimeType: value,
                                },
                              })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent position="popper">
                              <SelectItem value="adhan">Adhan</SelectItem>
                              <SelectItem value="iqama">Iqama</SelectItem>
                            </SelectContent>
                          </Select>
                        )}

                        <div className="space-y-2">
                          <Label>Offset</Label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant={eventData.prayerTimeConfig.endOffsetType === "minutes" ? "default" : "outline"}
                              className="flex-1"
                              onClick={() =>
                                setEventData({
                                  ...eventData,
                                  prayerTimeConfig: {
                                    ...eventData.prayerTimeConfig,
                                    endOffsetType: "minutes",
                                  },
                                })
                              }
                            >
                              Minutes
                            </Button>
                            <Button
                              type="button"
                              variant={eventData.prayerTimeConfig.endOffsetType === "round" ? "default" : "outline"}
                              className="flex-1"
                              onClick={() =>
                                setEventData({
                                  ...eventData,
                                  prayerTimeConfig: {
                                    ...eventData.prayerTimeConfig,
                                    endOffsetType: "round",
                                  },
                                })
                              }
                            >
                              Round Up
                            </Button>
                          </div>
                          {eventData.prayerTimeConfig.endOffsetType === "minutes" && (
                            <div className="grid grid-cols-3 gap-2">
                              {[-30, -15, 0, 15, 30, 60].map((offset) => (
                                <Button
                                  key={offset}
                                  type="button"
                                  variant={eventData.prayerTimeConfig.endOffsetMinutes === offset ? "default" : "outline"}
                                  size="sm"
                                  onClick={() =>
                                    setEventData({
                                      ...eventData,
                                      prayerTimeConfig: {
                                        ...eventData.prayerTimeConfig,
                                        endOffsetMinutes: offset,
                                      },
                                    })
                                  }
                                >
                                  {offset === 0 ? "Exactly" : offset > 0 ? `+${offset}` : offset}
                                </Button>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
                </div>
              </section>

              {/* Registration Settings Section (PRO Feature) */}
              {CRM_ENABLED && (
              <section className="space-y-4">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase" style={{ letterSpacing: "0.14em", color: BRAND.gold }}>
                  <Users className="h-4 w-4" />
                  Registration (PRO Feature)
                </div>
                <div className="space-y-4 rounded-xl border bg-muted/30 p-4">
                  {/* Event Mode Selection */}
                  <div className="space-y-3">
                    <Label className="text-xs text-muted-foreground uppercase">Event Mode</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setEventData({ ...eventData, eventMode: "normal" })}
                        className={`rounded-lg border-2 p-3 text-left transition-all ${
                          eventData.eventMode === "normal"
                            ? "border-primary bg-primary/5"
                            : "border-transparent bg-background hover:border-muted-foreground/30"
                        }`}
                      >
                        <div className="font-medium text-sm">Normal Event</div>
                        <div className="text-xs text-muted-foreground">Open to all, optional RSVP</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setEventData({ ...eventData, eventMode: "registration_required" })}
                        className={`rounded-lg border-2 p-3 text-left transition-all ${
                          eventData.eventMode === "registration_required"
                            ? "border-primary bg-primary/5"
                            : "border-transparent bg-background hover:border-muted-foreground/30"
                        }`}
                      >
                        <div className="font-medium text-sm flex items-center gap-1">
                          Registration Required
                          <Badge variant="secondary" className="text-xs">PRO</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">Requires sign-up, manage attendees</div>
                      </button>
                    </div>
                  </div>

                  {/* Registration Configuration (only when registration_required) */}
                  {eventData.eventMode === "registration_required" && (
                    <div className="space-y-4 pt-4 border-t">
                      {/* Capacity & Approval */}
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="maxParticipants">Maximum Participants</Label>
                          <Input
                            id="maxParticipants"
                            type="number"
                            min="1"
                            placeholder="Unlimited"
                            value={eventData.registrationConfig.maxParticipants || ""}
                            onChange={(e) => setEventData({
                              ...eventData,
                              registrationConfig: {
                                ...eventData.registrationConfig,
                                maxParticipants: e.target.value ? parseInt(e.target.value) : null,
                              },
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="registrationDeadline">Registration Deadline</Label>
                          <Select
                            value={String(eventData.registrationConfig.registrationDeadlineHours || "none")}
                            onValueChange={(value) => setEventData({
                              ...eventData,
                              registrationConfig: {
                                ...eventData.registrationConfig,
                                registrationDeadlineHours: value === "none" ? null : parseInt(value),
                              },
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="No deadline" />
                            </SelectTrigger>
                            <SelectContent position="popper">
                              <SelectItem value="none">No deadline</SelectItem>
                              <SelectItem value="1">1 hour before</SelectItem>
                              <SelectItem value="2">2 hours before</SelectItem>
                              <SelectItem value="6">6 hours before</SelectItem>
                              <SelectItem value="24">1 day before</SelectItem>
                              <SelectItem value="48">2 days before</SelectItem>
                              <SelectItem value="168">1 week before</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Approval & Waitlist Toggles */}
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={eventData.registrationConfig.requireApproval}
                            onCheckedChange={(checked) => setEventData({
                              ...eventData,
                              registrationConfig: {
                                ...eventData.registrationConfig,
                                requireApproval: checked,
                              },
                            })}
                          />
                          <span className="text-sm">Require manual approval</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={eventData.registrationConfig.allowWaitlist}
                            onCheckedChange={(checked) => setEventData({
                              ...eventData,
                              registrationConfig: {
                                ...eventData.registrationConfig,
                                allowWaitlist: checked,
                              },
                            })}
                          />
                          <span className="text-sm">Enable waitlist when full</span>
                        </label>
                      </div>

                      {/* Ticket Types */}
                      <div className="space-y-3 pt-3 border-t">
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-2">
                            <Ticket className="h-4 w-4" />
                            Ticket Types
                          </Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newTicket = {
                                id: `ticket_${Date.now()}`,
                                name: `Ticket ${eventData.registrationConfig.ticketTypes.length + 1}`,
                                description: "",
                                maxQuantity: null,
                                paymentType: "free",
                                paymentMethod: null,
                                paymentMode: null,
                                price: null,
                                customPaymentUrl: "",
                                customPaymentLabel: "Pay Now",
                                isActive: true,
                              };
                              setEventData({
                                ...eventData,
                                registrationConfig: {
                                  ...eventData.registrationConfig,
                                  ticketTypes: [...eventData.registrationConfig.ticketTypes, newTicket],
                                },
                              });
                            }}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Ticket
                          </Button>
                        </div>

                        {eventData.registrationConfig.ticketTypes.length === 0 ? (
                          <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
                            No ticket types defined. Free registration will be available.
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {eventData.registrationConfig.ticketTypes.map((ticket, index) => (
                              <div key={ticket.id} className="rounded-lg border bg-background p-4 space-y-3">
                                <div className="flex items-start justify-between gap-2">
                                  <Input
                                    value={ticket.name}
                                    onChange={(e) => {
                                      const updated = [...eventData.registrationConfig.ticketTypes];
                                      updated[index] = { ...updated[index], name: e.target.value };
                                      setEventData({
                                        ...eventData,
                                        registrationConfig: {
                                          ...eventData.registrationConfig,
                                          ticketTypes: updated,
                                        },
                                      });
                                    }}
                                    placeholder="Ticket name"
                                    className="font-medium"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      setEventData({
                                        ...eventData,
                                        registrationConfig: {
                                          ...eventData.registrationConfig,
                                          ticketTypes: eventData.registrationConfig.ticketTypes.filter((_, i) => i !== index),
                                        },
                                      });
                                    }}
                                    className="text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>

                                <div className="grid gap-3 sm:grid-cols-3">
                                  <div className="space-y-2">
                                    <Label className="text-xs">Max Quantity</Label>
                                    <Input
                                      type="number"
                                      min="1"
                                      placeholder="Unlimited"
                                      value={ticket.maxQuantity || ""}
                                      onChange={(e) => {
                                        const updated = [...eventData.registrationConfig.ticketTypes];
                                        updated[index] = { ...updated[index], maxQuantity: e.target.value ? parseInt(e.target.value) : null };
                                        setEventData({
                                          ...eventData,
                                          registrationConfig: {
                                            ...eventData.registrationConfig,
                                            ticketTypes: updated,
                                          },
                                        });
                                      }}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label className="text-xs">Payment</Label>
                                    <Select
                                      value={ticket.paymentType}
                                      onValueChange={(value) => {
                                        const updated = [...eventData.registrationConfig.ticketTypes];
                                        updated[index] = { 
                                          ...updated[index], 
                                          paymentType: value,
                                          paymentMethod: value === "paid" ? "custom" : null,
                                          paymentMode: value === "paid" ? "required" : null,
                                        };
                                        setEventData({
                                          ...eventData,
                                          registrationConfig: {
                                            ...eventData.registrationConfig,
                                            ticketTypes: updated,
                                          },
                                        });
                                      }}
                                    >
                                      <SelectTrigger>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent position="popper">
                                        <SelectItem value="free">Free</SelectItem>
                                        <SelectItem value="paid">Paid</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  {ticket.paymentType === "paid" && (
                                    <div className="space-y-2">
                                      <Label className="text-xs">Price ($)</Label>
                                      <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="0.00"
                                        value={ticket.price || ""}
                                        onChange={(e) => {
                                          const updated = [...eventData.registrationConfig.ticketTypes];
                                          updated[index] = { ...updated[index], price: e.target.value ? parseFloat(e.target.value) : null };
                                          setEventData({
                                            ...eventData,
                                            registrationConfig: {
                                              ...eventData.registrationConfig,
                                              ticketTypes: updated,
                                            },
                                          });
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>

                                {ticket.paymentType === "paid" && (
                                  <div className="space-y-2 pt-2 border-t">
                                    <Label className="text-xs">Payment Link URL</Label>
                                    <Input
                                      placeholder="https://your-payment-link.com"
                                      value={ticket.customPaymentUrl || ""}
                                      onChange={(e) => {
                                        const updated = [...eventData.registrationConfig.ticketTypes];
                                        updated[index] = { ...updated[index], customPaymentUrl: e.target.value };
                                        setEventData({
                                          ...eventData,
                                          registrationConfig: {
                                            ...eventData.registrationConfig,
                                            ticketTypes: updated,
                                          },
                                        });
                                      }}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                      External payment link (Zeffy, Stripe, PayPal, etc.)
                                    </p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Age/Gender Restrictions */}
                      <div className="space-y-3 pt-3 border-t">
                        <Label className="text-xs text-muted-foreground uppercase">Restrictions (Optional)</Label>
                        <div className="flex flex-wrap gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={eventData.registrationConfig.requireAge}
                              onCheckedChange={(checked) => setEventData({
                                ...eventData,
                                registrationConfig: {
                                  ...eventData.registrationConfig,
                                  requireAge: checked,
                                },
                              })}
                            />
                            <span className="text-sm">Age restrictions</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={eventData.registrationConfig.requireGender}
                              onCheckedChange={(checked) => setEventData({
                                ...eventData,
                                registrationConfig: {
                                  ...eventData.registrationConfig,
                                  requireGender: checked,
                                },
                              })}
                            />
                            <span className="text-sm">Gender restrictions</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={eventData.registrationConfig.waiverRequired}
                              onCheckedChange={(checked) => setEventData({
                                ...eventData,
                                registrationConfig: {
                                  ...eventData.registrationConfig,
                                  waiverRequired: checked,
                                },
                              })}
                            />
                            <span className="text-sm">Require waiver</span>
                          </label>
                        </div>

                        {eventData.registrationConfig.requireAge && (
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div className="space-y-2">
                              <Label className="text-xs">Minimum Age</Label>
                              <Input
                                type="number"
                                min="0"
                                placeholder="No minimum"
                                value={eventData.registrationConfig.minAge || ""}
                                onChange={(e) => setEventData({
                                  ...eventData,
                                  registrationConfig: {
                                    ...eventData.registrationConfig,
                                    minAge: e.target.value ? parseInt(e.target.value) : null,
                                  },
                                })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs">Maximum Age</Label>
                              <Input
                                type="number"
                                min="0"
                                placeholder="No maximum"
                                value={eventData.registrationConfig.maxAge || ""}
                                onChange={(e) => setEventData({
                                  ...eventData,
                                  registrationConfig: {
                                    ...eventData.registrationConfig,
                                    maxAge: e.target.value ? parseInt(e.target.value) : null,
                                  },
                                })}
                              />
                            </div>
                          </div>
                        )}

                        {eventData.registrationConfig.requireGender && (
                          <div className="space-y-2">
                            <Label className="text-xs">Allowed Genders</Label>
                            <div className="flex gap-3">
                              {["male", "female"].map((gender) => (
                                <label key={gender} className="flex items-center gap-2 cursor-pointer">
                                  <Checkbox
                                    checked={(eventData.registrationConfig.allowedGenders || []).includes(gender)}
                                    onCheckedChange={(checked) => {
                                      const current = eventData.registrationConfig.allowedGenders || [];
                                      const updated = checked
                                        ? [...current, gender]
                                        : current.filter(g => g !== gender);
                                      setEventData({
                                        ...eventData,
                                        registrationConfig: {
                                          ...eventData.registrationConfig,
                                          allowedGenders: updated,
                                        },
                                      });
                                    }}
                                  />
                                  <span className="text-sm capitalize">{gender}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}

                        {eventData.registrationConfig.waiverRequired && (
                          <div className="space-y-2">
                            <Label className="text-xs">Waiver Text</Label>
                            <Textarea
                              rows={3}
                              placeholder="Enter your waiver/liability agreement text..."
                              value={eventData.registrationConfig.waiverText || ""}
                              onChange={(e) => setEventData({
                                ...eventData,
                                registrationConfig: {
                                  ...eventData.registrationConfig,
                                  waiverText: e.target.value,
                                },
                              })}
                            />
                          </div>
                        )}
                      </div>

                      {/* Confirmation Message */}
                      <div className="space-y-2 pt-3 border-t">
                        <Label htmlFor="confirmationMessage">Confirmation Message (Optional)</Label>
                        <Textarea
                          id="confirmationMessage"
                          rows={2}
                          placeholder="Message shown after successful registration..."
                          value={eventData.registrationConfig.confirmationMessage || ""}
                          onChange={(e) => setEventData({
                            ...eventData,
                            registrationConfig: {
                              ...eventData.registrationConfig,
                              confirmationMessage: e.target.value,
                            },
                          })}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </section>
              )}

              {/* Recurrence Section */}
              <section className="space-y-4">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase" style={{ letterSpacing: "0.14em", color: BRAND.gold }}>
                  <Repeat className="h-4 w-4" />
                  Recurrence
                </div>
                <div className="space-y-4 rounded-xl border bg-muted/30 p-4">
                  <div className="space-y-3">
                    <Label className="text-xs text-muted-foreground uppercase">Repeat</Label>
                    <Select
                      value={eventData.recurrence.type}
                      onValueChange={(value) =>
                        setEventData({
                          ...eventData,
                          recurrence: { ...eventData.recurrence, type: value },
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent position="popper">
                        <SelectItem value="never">Does not repeat</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Customize Checkbox */}
                {(eventData.recurrence.type === "weekly" || eventData.recurrence.type === "monthly" || eventData.recurrence.type === "yearly") && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="customize-recurrence"
                      checked={eventData.recurrence.showCustomOptions || false}
                      onCheckedChange={(checked) =>
                        setEventData({
                          ...eventData,
                          recurrence: {
                            ...eventData.recurrence,
                            showCustomOptions: checked,
                          },
                        })
                      }
                    />
                    <Label htmlFor="customize-recurrence" className="text-sm cursor-pointer">
                      Customize recurrence
                    </Label>
                  </div>
                )}

                {eventData.recurrence.type === "weekly" && eventData.recurrence.showCustomOptions && (
                  <div className="space-y-2">
                    <Label className="text-sm">Repeat on</Label>
                    <div className="flex flex-wrap gap-2">
                      {WEEKDAY_NAMES.map((day, idx) => {
                        const dayCode = WEEKDAYS[idx];
                        const isSelected = eventData.recurrence.byDay.includes(dayCode);
                        return (
                          <Button
                            key={dayCode}
                            type="button"
                            variant={isSelected ? "default" : "outline"}
                            size="sm"
                            className="w-12"
                            onClick={() => {
                              const newByDay = isSelected
                                ? eventData.recurrence.byDay.filter((d) => d !== dayCode)
                                : [...eventData.recurrence.byDay, dayCode];
                              setEventData({
                                ...eventData,
                                recurrence: {
                                  ...eventData.recurrence,
                                  byDay: newByDay,
                                },
                              });
                            }}
                          >
                            {day.substring(0, 2)}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Monthly Custom Options */}
                {eventData.recurrence.type === "monthly" && eventData.recurrence.showCustomOptions && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Select which week(s) of the month</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { label: "First", value: 1 },
                          { label: "Second", value: 2 },
                          { label: "Third", value: 3 },
                          { label: "Fourth", value: 4 },
                          { label: "Next to Last", value: -2 },
                          { label: "Last", value: -1 },
                        ].map((pos) => {
                          const isSelected = (eventData.recurrence.bySetPos || []).includes(pos.value);
                          return (
                            <Button
                              key={pos.value}
                              type="button"
                              variant={isSelected ? "default" : "outline"}
                              size="sm"
                              onClick={() => {
                                const current = eventData.recurrence.bySetPos || [];
                                const newBySetPos = isSelected
                                  ? current.filter((p) => p !== pos.value)
                                  : [pos.value];
                                setEventData({
                                  ...eventData,
                                  recurrence: {
                                    ...eventData.recurrence,
                                    bySetPos: newBySetPos,
                                  },
                                });
                              }}
                            >
                              {pos.label}
                            </Button>
                          );
                        })}
                      </div>
                    </div>

                    {eventData.recurrence.bySetPos && eventData.recurrence.bySetPos.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-sm">Select which day(s) of the week</Label>
                        <div className="flex flex-wrap gap-2">
                          {WEEKDAY_NAMES.map((day, idx) => {
                            const dayCode = WEEKDAYS[idx];
                            const isSelected = (eventData.recurrence.byDay || []).includes(dayCode);
                            return (
                              <Button
                                key={dayCode}
                                type="button"
                                variant={isSelected ? "default" : "outline"}
                                size="sm"
                                className="w-12"
                                onClick={() => {
                                  const current = eventData.recurrence.byDay || [];
                                  const newByDay = isSelected
                                    ? current.filter((d) => d !== dayCode)
                                    : [dayCode];
                                  setEventData({
                                    ...eventData,
                                    recurrence: {
                                      ...eventData.recurrence,
                                      byDay: newByDay,
                                    },
                                  });
                                }}
                              >
                                {day.substring(0, 2)}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Yearly Custom Options */}
                {eventData.recurrence.type === "yearly" && eventData.recurrence.showCustomOptions && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Select which month(s) of the year</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { label: "Jan", value: 1 },
                          { label: "Feb", value: 2 },
                          { label: "Mar", value: 3 },
                          { label: "Apr", value: 4 },
                          { label: "May", value: 5 },
                          { label: "Jun", value: 6 },
                          { label: "Jul", value: 7 },
                          { label: "Aug", value: 8 },
                          { label: "Sep", value: 9 },
                          { label: "Oct", value: 10 },
                          { label: "Nov", value: 11 },
                          { label: "Dec", value: 12 },
                        ].map((month) => {
                          const isSelected = (eventData.recurrence.byMonth || []).includes(month.value);
                          return (
                            <Button
                              key={month.value}
                              type="button"
                              variant={isSelected ? "default" : "outline"}
                              size="sm"
                              onClick={() => {
                                const current = eventData.recurrence.byMonth || [];
                                const newByMonth = isSelected
                                  ? current.filter((m) => m !== month.value)
                                  : [...current, month.value];
                                setEventData({
                                  ...eventData,
                                  recurrence: {
                                    ...eventData.recurrence,
                                    byMonth: newByMonth,
                                  },
                                });
                              }}
                            >
                              {month.label}
                            </Button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Select which week(s) of the month</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { label: "First", value: 1 },
                          { label: "Second", value: 2 },
                          { label: "Third", value: 3 },
                          { label: "Fourth", value: 4 },
                          { label: "Next to Last", value: -2 },
                          { label: "Last", value: -1 },
                        ].map((pos) => {
                          const isSelected = (eventData.recurrence.bySetPos || []).includes(pos.value);
                          return (
                            <Button
                              key={pos.value}
                              type="button"
                              variant={isSelected ? "default" : "outline"}
                              size="sm"
                              onClick={() => {
                                const current = eventData.recurrence.bySetPos || [];
                                const newBySetPos = isSelected
                                  ? current.filter((p) => p !== pos.value)
                                  : [pos.value];
                                setEventData({
                                  ...eventData,
                                  recurrence: {
                                    ...eventData.recurrence,
                                    bySetPos: newBySetPos,
                                  },
                                });
                              }}
                            >
                              {pos.label}
                            </Button>
                          );
                        })}
                      </div>
                    </div>

                    {eventData.recurrence.bySetPos && eventData.recurrence.bySetPos.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-sm">Select which day(s) of the week</Label>
                        <div className="flex flex-wrap gap-2">
                          {WEEKDAY_NAMES.map((day, idx) => {
                            const dayCode = WEEKDAYS[idx];
                            const isSelected = (eventData.recurrence.byDay || []).includes(dayCode);
                            return (
                              <Button
                                key={dayCode}
                                type="button"
                                variant={isSelected ? "default" : "outline"}
                                size="sm"
                                className="w-12"
                                onClick={() => {
                                  const current = eventData.recurrence.byDay || [];
                                  const newByDay = isSelected
                                    ? current.filter((d) => d !== dayCode)
                                    : [dayCode];
                                  setEventData({
                                    ...eventData,
                                    recurrence: {
                                      ...eventData.recurrence,
                                      byDay: newByDay,
                                    },
                                  });
                                }}
                              >
                                {day.substring(0, 2)}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {eventData.recurrence.type !== "never" && (
                  <div className="space-y-2">
                    <Label htmlFor="recurrence-until">Ends on (optional)</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          id="recurrence-until"
                          type="button"
                          variant="outline"
                          className="w-full justify-start font-normal"
                        >
                          <Calendar className="mr-2 h-4 w-4" style={{ color: BRAND.gold }} />
                          {eventData.recurrence.until ? (
                            format(new Date(eventData.recurrence.until), "PPP")
                          ) : (
                            <span className="text-muted-foreground">No end date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[100001]" align="start">
                        <CalendarPicker
                          mode="single"
                          selected={
                            eventData.recurrence.until
                              ? new Date(eventData.recurrence.until)
                              : undefined
                          }
                          onSelect={(date) =>
                            setEventData({
                              ...eventData,
                              recurrence: {
                                ...eventData.recurrence,
                                until: date || null,
                              },
                            })
                          }
                          autoFocus
                        />
                        {eventData.recurrence.until && (
                          <div className="border-t p-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="w-full"
                              onClick={() =>
                                setEventData({
                                  ...eventData,
                                  recurrence: {
                                    ...eventData.recurrence,
                                    until: null,
                                  },
                                })
                              }
                            >
                              Clear
                            </Button>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
                </div>
              </section>

              {/* Details Section */}
              <section className="space-y-4">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase" style={{ letterSpacing: "0.14em", color: BRAND.gold }}>
                  <Settings className="h-4 w-4" />
                  Additional Details
                </div>
                <div className="space-y-4 rounded-xl border bg-muted/30 p-4">
                  <div className="space-y-2">
                    <Label htmlFor="location">Location <span className="text-muted-foreground text-xs">(Optional)</span></Label>
                    <Input
                      id="location"
                      value={eventData.location}
                      onChange={(e) =>
                        setEventData({ ...eventData, location: e.target.value })
                      }
                      placeholder="e.g., Main Prayer Hall"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Details <span className="text-muted-foreground text-xs">(Optional)</span></Label>
                    <Textarea
                      id="description"
                      rows={4}
                      value={eventData.description}
                      onChange={(e) =>
                        setEventData({ ...eventData, description: e.target.value })
                      }
                      placeholder="Provide details about the event..."
                      className="resize-none"
                    />
                  </div>
                </div>
              </section>
            </div>
            <DialogFooter className="gap-2 shrink-0 pt-4" style={{ borderTop: `1px solid ${palette.hairline}` }}>
              <PillButton
                variant="outline"
                onClick={() => setEventDialogOpen(false)}
                disabled={saving}
              >
                Cancel
              </PillButton>
              <PillButton variant="primary" onClick={handleSaveEvent} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    {editingEvent ? "Save changes" : "Create event"}
                    <span aria-hidden>→</span>
                  </>
                )}
              </PillButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Google Calendar Management Dialog */}
        <Dialog open={calendarDialogOpen} onOpenChange={(open) => {
          if (!open && !calendarSettingsOpen) {
            setCalendarDialogOpen(false);
          }
        }}>
          <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle
                style={{
                  fontFamily: EDITORIAL_FONT.serif,
                  fontSize: 26,
                  fontWeight: 500,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                }}
              >
                Google <em style={{ color: BRAND.gold, fontWeight: 400 }}>calendars.</em>
              </DialogTitle>
              <DialogDescription style={{ color: palette.muted }}>
                Add public Google Calendar IDs to automatically sync events to
                your masjid.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 overflow-y-auto flex-1 pr-2">
              <div className="flex gap-2">
                <Input
                  placeholder="example@group.calendar.google.com"
                  value={newCalendarId}
                  onChange={(e) => setNewCalendarId(e.target.value)}
                />
                <Button
                  onClick={handleAddCalendar}
                  disabled={!newCalendarId.trim()}
                >
                  Add
                </Button>
              </div>

              {calendars.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold">Connected Calendars:</p>
                  <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                    {calendars.map((calendar) => (
                      <div
                        key={calendar.calendarId}
                        className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border bg-card p-4 transition-colors hover:bg-accent/50"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {calendar.nickname || calendar.calendarId}
                          </p>
                          {calendar.nickname && (
                            <p className="text-xs text-muted-foreground truncate">
                              {calendar.calendarId}
                            </p>
                          )}
                          <div className="mt-1 flex flex-wrap gap-2">
                            {calendar.addedAt && (
                              <p className="text-xs text-muted-foreground">
                                Added{" "}
                                {new Date(calendar.addedAt).toLocaleDateString()}
                              </p>
                            )}
                            {(calendar.timeOffset !== undefined && calendar.timeOffset !== 0) && (
                              <Badge variant="outline" className="text-xs">
                                <Clock className="mr-1 h-3 w-3" />
                                {calendar.timeOffset > 0 ? '+' : ''}{calendar.timeOffset}h
                              </Badge>
                            )}
                            {calendar.filters && calendar.filters.length > 0 && (
                              <Badge variant="outline" className="text-xs">
                                <Filter className="mr-1 h-3 w-3" />
                                {calendar.filters.length} filter{calendar.filters.length !== 1 ? 's' : ''}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 self-end sm:self-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenCalendarSettings(calendar)}
                            title="Calendar Settings"
                            className="shrink-0 hover:bg-blue-100 hover:text-blue-700 dark:hover:bg-blue-900/50"
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              handleRemoveCalendar(calendar.calendarId)
                            }
                            title="Remove Calendar"
                            className="shrink-0 text-red-600 hover:bg-red-100 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="pt-4 border-t">
              <Button onClick={() => setCalendarDialogOpen(false)} className="w-full sm:w-auto">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Calendar Settings Dialog */}
        <Dialog open={calendarSettingsOpen} onOpenChange={(open) => {
          if (!open) {
            setCalendarSettingsOpen(false);
            setSelectedCalendar(null);
          }
        }}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle
                style={{
                  fontFamily: EDITORIAL_FONT.serif,
                  fontSize: 26,
                  fontWeight: 500,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                }}
              >
                Calendar <em style={{ color: BRAND.gold, fontWeight: 400 }}>settings.</em>
              </DialogTitle>
              <DialogDescription style={{ color: palette.muted }}>
                Configure settings for {selectedCalendar?.nickname || selectedCalendar?.calendarId}
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto space-y-6 py-4">
              {/* Calendar Nickname */}
              <div className="space-y-2">
                <Label htmlFor="nickname">Calendar Nickname</Label>
                <Input
                  id="nickname"
                  value={calendarNickname}
                  onChange={(e) => setCalendarNickname(e.target.value)}
                  placeholder="e.g., Community Events"
                />
                <p className="text-xs text-muted-foreground">
                  Give this calendar a friendly name for easy identification
                </p>
              </div>

              {/* Time Offset */}
              <div className="space-y-2">
                <Label htmlFor="timeOffset">Time Offset (hours)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="timeOffset"
                    type="number"
                    value={calendarTimeOffset}
                    onChange={(e) => setCalendarTimeOffset(parseInt(e.target.value) || 0)}
                    className="w-32"
                    min="-12"
                    max="12"
                  />
                  <span className="text-sm text-muted-foreground">
                    {calendarTimeOffset === 0 ? 'No offset' : `${calendarTimeOffset > 0 ? '+' : ''}${calendarTimeOffset} hour${Math.abs(calendarTimeOffset) !== 1 ? 's' : ''}`}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Adjust event times if the calendar is in a different timezone
                </p>
              </div>

              {/* Event Filters */}
              <div className="space-y-3">
                <div>
                  <Label>Event Filters</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Automatically exclude events matching these keywords
                  </p>
                </div>

                {/* Add Filter */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter keyword to filter..."
                    value={newFilterKeyword}
                    onChange={(e) => setNewFilterKeyword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddFilter()}
                  />
                  <Button
                    onClick={handleAddFilter}
                    disabled={!newFilterKeyword.trim()}
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>

                {/* Filter Options */}
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newFilterPartial}
                      onChange={(e) => setNewFilterPartial(e.target.checked)}
                      className="rounded"
                    />
                    Partial match
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newFilterCaseSensitive}
                      onChange={(e) => setNewFilterCaseSensitive(e.target.checked)}
                      className="rounded"
                    />
                    Case sensitive
                  </label>
                </div>

                {/* Filter List */}
                {calendarFilters.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    <p className="text-sm font-medium">Active Filters:</p>
                    {calendarFilters.map((filter, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 border rounded-md bg-muted/50"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium">{filter.keyword}</p>
                          <p className="text-xs text-muted-foreground">
                            {filter.partial ? 'Partial' : 'Full'} match •{' '}
                            {filter.caseSensitive ? 'Case sensitive' : 'Case insensitive'}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFilter(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setCalendarSettingsOpen(false);
                  setSelectedCalendar(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveCalendarSettings}
                disabled={settingsSaving}
              >
                {settingsSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Settings'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Scope Dialog for Recurring Events */}
        <Dialog open={editScopeDialogOpen} onOpenChange={setEditScopeDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle
                style={{
                  fontFamily: EDITORIAL_FONT.serif,
                  fontSize: 26,
                  fontWeight: 500,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                }}
              >
                Edit recurring{" "}
                <em style={{ color: BRAND.gold, fontWeight: 400 }}>event.</em>
              </DialogTitle>
              <DialogDescription style={{ color: palette.muted }}>
                This is a recurring event. What would you like to edit?
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 py-4">
              <Button
                variant="outline"
                onClick={() => confirmEditScope("single")}
                className="justify-start h-auto p-4"
              >
                <div className="text-left">
                  <div className="font-semibold">Edit this occurrence</div>
                  <div className="text-sm text-muted-foreground">Only this specific event will be updated</div>
                </div>
              </Button>
              <Button
                variant="outline"
                onClick={() => confirmEditScope("series")}
                className="justify-start h-auto p-4"
              >
                <div className="text-left">
                  <div className="font-semibold">Edit entire series</div>
                  <div className="text-sm text-muted-foreground">All events in this series will be updated</div>
                </div>
              </Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditScopeDialogOpen(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Scope Dialog for Recurring Events */}
        <Dialog open={deleteScopeDialogOpen} onOpenChange={setDeleteScopeDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle
                style={{
                  fontFamily: EDITORIAL_FONT.serif,
                  fontSize: 26,
                  fontWeight: 500,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                }}
              >
                Delete recurring{" "}
                <em style={{ color: BRAND.gold, fontWeight: 400 }}>event.</em>
              </DialogTitle>
              <DialogDescription style={{ color: palette.muted }}>
                This is a recurring event. What would you like to delete?
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 py-4">
              <Button
                variant="outline"
                onClick={() => confirmDeleteScope("single")}
                className="justify-start h-auto p-4"
              >
                <div className="text-left">
                  <div className="font-semibold">Delete this occurrence</div>
                  <div className="text-sm text-muted-foreground">Only this specific event will be deleted</div>
                </div>
              </Button>
              <Button
                variant="outline"
                onClick={() => confirmDeleteScope("series")}
                className="justify-start h-auto p-4 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
              >
                <div className="text-left">
                  <div className="font-semibold">Delete entire series</div>
                  <div className="text-sm text-muted-foreground">All events in this series will be deleted</div>
                </div>
              </Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteScopeDialogOpen(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Final delete confirmation */}
        <Dialog
          open={!!pendingDeletion}
          onOpenChange={(open) => {
            if (!open && !deletingNow) setPendingDeletion(null);
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                  style={{
                    background: BRAND.terracotta + "1f",
                    color: BRAND.terracotta,
                  }}
                >
                  <Trash2 className="h-5 w-5" />
                </div>
                <DialogTitle
                  style={{
                    fontFamily: EDITORIAL_FONT.serif,
                    fontSize: 24,
                    fontWeight: 500,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.1,
                  }}
                >
                  Delete {pendingDeletion?.scope === "series" ? "series" : "event"}.
                </DialogTitle>
              </div>
              <DialogDescription className="pt-3" style={{ color: palette.muted }}>
                {pendingDeletion?.scope === "series" ? (
                  <>
                    This will permanently delete <span className="font-medium" style={{ color: palette.ink }}>{pendingDeletion?.title}</span> and every occurrence in the series. This action cannot be undone.
                  </>
                ) : (
                  <>
                    This will permanently delete <span className="font-medium" style={{ color: palette.ink }}>{pendingDeletion?.title}</span>. This action cannot be undone.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setPendingDeletion(null)}
                disabled={deletingNow}
              >
                Cancel
              </Button>
              <Button
                onClick={executePendingDelete}
                disabled={deletingNow}
                style={{
                  background: BRAND.terracotta,
                  color: "#fff",
                  border: `1px solid ${BRAND.terracotta}`,
                }}
                className="hover:opacity-90"
              >
                {deletingNow ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete {pendingDeletion?.scope === "series" ? "series" : "event"}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </EditorialShell>
  );
}
